import { Layer } from "@common/constants";
// import { equalLayer, isGroundLayer } from "@common/utils/layer";
import { Numeric } from "@common/utils/math";
import { Vec, type Vector } from "@common/utils/vector";
import { Game } from "../game";
// add a namespace to pixi sound imports because it has annoying generic names like "sound" and "filters" without a namespace
import * as PixiSound from "@pixi/sound";
import type { AudioSpritesheetImporter } from "../../../vite/plugins/audio-spritesheet-plugin";
import { GameConsole } from "../console/gameConsole";
import type { Tween } from "../utils/tween";
import { Modes } from "@common/definitions/modes";
import { WarAudio } from "../war/warAudio";
import { Acoustics } from "./acoustics";
import { AIR_OPEN, airCutoff, roomSend } from "./combatSoundsLogic";

export interface SoundOptions {
    position?: Vector
    falloff: number
    layer: Layer | number
    maxRange: number
    loop: boolean
    speed?: number
    /**
     * If the sound volume and panning will be updated
     * when the camera position changes after it started playing
     */
    dynamic: boolean
    ambient: boolean
    onEnd?: () => void
    /** how loud it plays, against its full loudness (1) */
    volume?: number
    /**
     * played a little differently each time, as no two real shots or impacts sound the same: its pitch (speed) and
     * its loudness each vary by up to this fraction either way
     */
    variation?: { readonly pitch?: number, readonly volume?: number }
    /** the air (and whatever's in the way) dulls it with distance: a lowpass, lower the farther it is (airCutoff) */
    air?: boolean
    /** this share of it reaches the echo of its surroundings (acoustics.ts; nothing when 0 or left out) */
    room?: number
}

/**
 * the old HTML audio fallback takes a volume of at most 1 (an audio element throws above it): a gunshot played a little
 * louder (its variation), or a shell's casing (1.2), is held to 1 there (0.85)
 */
const legacyCap = (volume: number): number => (PixiSound.sound.useLegacy ? Math.min(1, volume) : volume);

/** a number between -1 and 1 */
const either = (): number => Math.random() * 2 - 1;

/**
 * The air's dulling of a sound (one lowpass). A filter in the sound library's chain: a sound's own nodes, made when it
 * starts and dropped when it ends.
 */
class AirFilter extends PixiSound.Filter {
    private readonly _lowpass: BiquadFilterNode;
    private readonly _nyquist: number;

    constructor(context: AudioContext, cutoff: number) {
        const lowpass = context.createBiquadFilter();
        lowpass.type = "lowpass";
        // (a lowpass's Q is in decibels in Web Audio: -4.4 dB is a Q of 0.6, a gentle slope without a peak. 0.84's first
        // build set 0.6 dB, a slight peak at the cutoff: the audit)
        lowpass.Q.value = -4.4;
        super(lowpass);
        this._lowpass = lowpass;
        this._nyquist = context.sampleRate / 2;
        this.cutoff = cutoff;
    }

    private _cutoff?: number;

    set cutoff(hz: number) {
        // (0.98.8: set only when it changes: a moving or looping sound's every update set it again)
        const value = Math.min(hz, this._nyquist * 0.95);
        if (value === this._cutoff) return;
        this._cutoff = value;
        this._lowpass.frequency.value = value;
    }
}

/**
 * A sound's send to the echo of its surroundings: passes the sound on unchanged, and a share of it (its `level`) to the
 * shared echo (acoustics.ts). Its share is taken before the sound's own volume, so the level carries it (GameSound sets
 * it with the volume).
 */
class RoomSend extends PixiSound.Filter {
    private readonly _send: GainNode;

    constructor(context: AudioContext, bus: AudioNode) {
        const through = context.createGain();
        const send = context.createGain();
        send.gain.value = 0;
        through.connect(send);
        send.connect(bus);
        super(through);
        this._send = send;
    }

    private _level?: number;

    set level(value: number) {
        if (value === this._level) return;
        this._level = value;
        this._send.gain.value = value;
    }

    override disconnect(): void {
        super.disconnect();
        // (off the shared bus too, so nothing of the finished sound stays connected to it)
        this._send.disconnect();
    }
}

PixiSound.sound.disableAutoPause = true;

export class GameSound {
    id: string;
    name: string;
    position?: Vector;
    falloff: number;
    maxRange: number;
    layer: Layer | number;
    speed: number;
    onEnd?: () => void;

    readonly dynamic: boolean;
    readonly ambient: boolean;

    get managerVolume(): number { return this.ambient ? SoundManager.ambienceVolume : SoundManager.sfxVolume; }

    // acts as multiplier
    volume = 1;
    layerMult = 1;

    layerVolumeTween?: Tween<GameSound>;

    instance?: PixiSound.IMediaInstance;
    /** made only for a sound that plays (one out of earshot never does) */
    readonly stereoFilter?: PixiSound.filters.StereoFilter;
    private readonly _air?: AirFilter;
    private readonly _room?: RoomSend;
    /** its share of the echo (SoundOptions.room) */
    private readonly _roomAmount: number;

    ended = false;

    constructor(id: string, name: string, options: SoundOptions) {
        this.id = id;
        this.name = name;
        this.position = options.position;
        this.falloff = options.falloff;
        this.maxRange = options.maxRange;
        this.layer = options.layer;
        this.speed = (options.speed ?? 1) * (1 + either() * (options.variation?.pitch ?? 0));
        this.volume = (options.volume ?? 1) * (1 + either() * (options.variation?.volume ?? 0));
        this.dynamic = options.dynamic;
        this.ambient = options.ambient;
        this.onEnd = options.onEnd;
        this._roomAmount = options.room ?? 0;

        if (!PixiSound.sound.exists(id)) {
            console.warn(`Unknown sound with name ${id}`);
            return;
        }

        // a sound that's out of earshot when it starts, and never changes, is never heard: it isn't played at all
        // (a far-off firefight was hundreds of silent sounds playing)
        const distance = this.position ? Vec.len(Vec.sub(SoundManager.position, this.position)) : 0;
        if (this.position && !this.dynamic && !this.ambient && !options.loop && !this.onEnd && distance >= this.maxRange) {
            this.ended = true;
            return;
        }

        const filters: PixiSound.Filter[] = [];
        if (!PixiSound.sound.useLegacy) {
            const context = PixiSound.sound.context.audioContext;
            // the air's dulling: none for a sound that starts close and stays put (it would change nothing)
            const cutoff = this.position ? airCutoff(distance, this.maxRange) : AIR_OPEN;
            if (options.air && this.position && (this.dynamic || cutoff < AIR_OPEN)) filters.push(this._air = new AirFilter(context, cutoff));
            const bus = this._roomAmount > 0 ? Acoustics.input() : undefined;
            if (bus) filters.push(this._room = new RoomSend(context, bus));
        }
        filters.push(this.stereoFilter = new PixiSound.filters.StereoFilter(0));

        const instanceOrPromise = PixiSound.sound.play(id, {
            filters,
            loop: options.loop,
            volume: legacyCap(this.managerVolume * this.volume),
            speed: this.speed
        });

        // PixiSound.sound.play returns a promise while the sound is still being decoded (the first time it plays: the
        // sounds are decoded when first used), and the instance comes with the promise. It used to be taken from the
        // `loaded` callback, which pixi calls only for the first of several plays made in that moment: the others'
        // instances were never known here, so they couldn't be stopped. A fire across two or three chunks starts two
        // or three crackle loops at once, and all but one crackled until the match ended.
        if (instanceOrPromise instanceof Promise) {
            void instanceOrPromise.then(
                instance => {
                    if (instance) this.init(instance);
                    else this.ended = true;
                },
                () => {
                    this.ended = true;
                }
            );
        } else {
            this.init(instanceOrPromise);
        }
    }

    init(instance: PixiSound.IMediaInstance): void {
        // one instance per sound: a second one arriving is stopped (never left playing with nothing to stop it)
        if (this.instance) {
            if (instance !== this.instance) instance.stop();
            return;
        }
        this.instance = instance;
        // stopped while it was still loading: it arrives now, so stop it now (a loop would otherwise never end)
        if (this.ended) {
            instance.stop();
            return;
        }
        instance.on("end", () => {
            this.onEnd?.();
            this.ended = true;
        });
        instance.on("stop", () => {
            this.ended = true;
        });
        this.updateLayer(true);
        this.update();
    }

    /**
     * the instance's volume set, only when it changes (0.98.8): pixi-sound's setter disconnects the sound and connects
     * its every filter again, and a moving or looping sound's every update (40 a second) set it, the same or not; a
     * change of the master or the sound's own volume reaches it through pixi-sound itself
     */
    private _volume(volume: number): void {
        if (this.instance && this.instance.volume !== volume) this.instance.volume = volume;
    }

    update(): void {
        if (!this.instance) return;

        const own = this.managerVolume * this.volume * this.layerMult;
        let direct = 1;
        if (this.position) {
            const diff = Vec.sub(SoundManager.position, this.position);
            const distance = Vec.len(diff);

            direct = (1 - Numeric.clamp(Math.abs(distance / this.maxRange), 0, 1)) ** (1 + this.falloff * 2);
            this._volume(legacyCap(direct * own));

            const pan = Numeric.clamp(-diff.x / this.maxRange, -1, 1);
            if (this.stereoFilter && this.stereoFilter.pan !== pan) this.stereoFilter.pan = pan;
            if (this._air) this._air.cutoff = airCutoff(distance, this.maxRange);
        } else {
            this._volume(legacyCap(own));
        }
        // its echo's share goes in before the sound's own volume (see RoomSend), so it carries the sound's volumes
        // itself (the master volume is the echo's own: acoustics.ts)
        if (this._room) this._room.level = roomSend(direct, this._roomAmount, Acoustics.law) * own;
    }

    updateLayer(initial = false): void {
        if (!this.instance) return;

        let layerMult = 1;
        const layerDelta = this.ambient // ambient sounds only decrease in volume underground
            ? this.layer - Game.layer
            : Math.abs(this.layer - Game.layer);
        if (layerDelta === 1) {
            layerMult = 0.5;
        } else if (layerDelta >= 2) {
            layerMult = 0.15;
        }

        if (initial) {
            this.layerMult = layerMult;
            return;
        }

        this.layerVolumeTween?.kill();
        this.layerVolumeTween = Game.addTween<GameSound>({
            target: this,
            to: { layerMult },
            duration: 500,
            onComplete: () => {
                this.layerVolumeTween = undefined;
            }
        });
    }

    setPaused(paused: boolean): void {
        if (this.instance) this.instance.paused = paused;
    }

    stop(): void {
        // trying to stop a sound that already ended or was stopped will stop a random sound
        // (maybe a bug? idk)
        if (this.ended) return;
        this.instance?.stop();
        this.ended = true;
    }
}

class SoundManagerClass {
    readonly updatableSounds = new Set<GameSound>();

    sfxVolume = 0;
    ambienceVolume = 0;

    position = Vec(0, 0);

    private _initialized = false;
    async init(): Promise<void> {
        if (this._initialized) {
            throw new Error("SoundManager has already been initialized");
        }
        this._initialized = true;

        this.sfxVolume = GameConsole.getBuiltInCVar("cv_sfx_volume");
        this.ambienceVolume = GameConsole.getBuiltInCVar("cv_ambience_volume");

        const { importSpritesheet } = await import("virtual:audio-spritesheet-importer") as AudioSpritesheetImporter;
        const _modeName = Modes[Game.modeName].similarTo ?? Game.modeName;
        const { filename, spritesheet } = await importSpritesheet(_modeName);
        const audio = await (await fetch(filename)).arrayBuffer();
        let offset = 0;
        for (const [id, length] of Object.entries(spritesheet)) {
            this.addSound(id, { source: audio.slice(offset, offset + length) });
            offset += length;
        }

        const { noPreloadSounds } = await import("virtual:audio-spritesheet-no-preload") as { noPreloadSounds: string[] };
        for (const id of noPreloadSounds) {
            this.addSound(id, { url: `./audio/game/no-preload/${id}.mp3`, preload: false });
        }
        // the echoes' rooms, made while nothing's going on (not at the first gunshot of a visit, mid-fight)
        Acoustics.prepareSoon();
    }

    addSound(id: string, opts: Partial<PixiSound.Options>): void {
        /**
         * For some reason, PIXI will call the `loaded` callback twice
         * when an error occurs…
         */
        let called = false;

        PixiSound.sound.add(id, {
            ...opts,
            loaded(error: Error | null) {
                // despite what the pixi typings say, logging `error` shows that it can be null
                if (error !== null && !called) {
                    called = true;
                    console.warn(`Failed to load sound '${id}'\nError object provided below`);
                    console.error(error);
                }
            }
        });
    }

    update(): void {
        Acoustics.update();
        for (const sound of this.updatableSounds) {
            if (sound.ended) {
                this.updatableSounds.delete(sound);
                continue;
            }
            sound.update();
        }
    }

    has(name: string): boolean {
        return PixiSound.sound.exists(name);
    }

    play(name: string, options?: Partial<SoundOptions>): GameSound {
        const sound = new GameSound(name, name, {
            falloff: 1,
            maxRange: 256,
            dynamic: false,
            ambient: false,
            layer: Game.layer,
            loop: false,
            ...options
        });

        if (sound.dynamic || sound.ambient) {
            this.updatableSounds.add(sound);
        }

        return sound;
    }

    stopAll(): void {
        PixiSound.sound.stopAll();
        this.updatableSounds.clear();
        // (the echoes still ringing stop too: they went on over the menu, a match left)
        Acoustics.hush();
        // (and the skies' and the gas's own: the rain, the wind, a thunder still rolling in: 1.06.1's audit)
        WarAudio.hush();
    }
}

export const SoundManager = new SoundManagerClass();
